import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Check, ChevronDown, ChevronUp, Loader2, Trash2 } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import {
  applyRateChange,
  computeTotals,
  nightsCount,
  rowHasError,
  step1Schema,
  step3Schema,
  type LookupResult,
  type RoomRow,
  type RoomType,
  type Step1Values,
  type Step3Values,
} from './booking-wizard.utils';
import { useAuthStore } from '@/store/auth.store';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Spinner } from '@/components/ui/spinner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PageHeader, BackLink } from '@/layouts/AppLayout';
import { cn } from '@/lib/utils';

// ── Constants ──────────────────────────────────────────────────────────────────

const MEAL_PLANS = ['CP', 'MAP', 'AP', 'APAI', 'MAPAI', 'CPI', 'EP'] as const;
const OCCUPANCY_TYPES = ['single', 'double', 'triple', 'quad', 'child'] as const;
const DRAFT_KEY = 'booking-draft';

function formatINR(amount: number) {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
  }).format(amount);
}

function newRow(): RoomRow {
  return {
    id: crypto.randomUUID(),
    hotelRoomTypeId: '',
    mealPlan: '',
    occupancyType: '',
    guestsPerRoom: 1,
    roomsCount: 1,
    ratePerNight: '',
    rateSource: 'unset',
    rateOverrideReason: '',
    contractedRateId: null,
    segments: undefined,
    isLookupLoading: false,
    maxOccupancy: null,
  };
}


// ── Combobox ───────────────────────────────────────────────────────────────────

interface ComboOption {
  id: string;
  label: string;
  sublabel?: string;
}

function Combobox({
  placeholder,
  value,
  onChange,
  options,
  isLoading,
  onSearch,
  searchValue,
}: {
  placeholder: string;
  value: ComboOption | null;
  onChange: (opt: ComboOption | null) => void;
  options: ComboOption[];
  isLoading?: boolean;
  onSearch: (q: string) => void;
  searchValue: string;
}) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <Input
        placeholder={placeholder}
        value={open ? searchValue : (value?.label ?? searchValue)}
        onChange={(e) => {
          onSearch(e.target.value);
          if (!open) setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        autoComplete="off"
      />
      {open && (
        <div className="absolute top-full left-0 z-50 mt-1 w-full rounded-md border border-neutral-200 bg-white shadow-lg">
          {isLoading ? (
            <div className="flex justify-center p-3">
              <Spinner className="h-4 w-4" />
            </div>
          ) : options.length === 0 ? (
            <p className="p-3 text-sm text-neutral-400">No results</p>
          ) : (
            <ul className="max-h-56 overflow-auto py-1">
              {options.map((opt) => (
                <li key={opt.id}>
                  <button
                    type="button"
                    className="flex w-full flex-col px-3 py-2 text-left hover:bg-neutral-50"
                    onClick={() => {
                      onChange(opt);
                      setOpen(false);
                    }}
                  >
                    <span className="text-sm font-medium text-neutral-900">{opt.label}</span>
                    {opt.sublabel && (
                      <span className="text-xs text-neutral-500">{opt.sublabel}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

// ── Step indicator ─────────────────────────────────────────────────────────────

const STEPS = ['Foundation', 'Rooms', 'Details', 'Review'];

function StepIndicator({ current }: { current: number }) {
  return (
    <nav aria-label="Booking wizard steps" className="flex items-center gap-0 border-b border-neutral-200 bg-white px-6">
      {STEPS.map((label, i) => {
        const stepNum = i + 1;
        const isActive = stepNum === current;
        const isDone = stepNum < current;
        return (
          <div key={label} className="flex items-center">
            <div
              className={cn(
                'flex items-center gap-2 px-4 py-4 text-sm font-medium border-b-2',
                isActive
                  ? 'border-[#F57C1F] text-[#F57C1F]'
                  : isDone
                  ? 'border-transparent text-neutral-500'
                  : 'border-transparent text-neutral-400',
              )}
              aria-current={isActive ? 'step' : undefined}
            >
              <span
                className={cn(
                  'flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold',
                  isActive
                    ? 'bg-[#F57C1F] text-white'
                    : isDone
                    ? 'bg-neutral-200 text-neutral-600'
                    : 'border border-neutral-300 text-neutral-400',
                )}
              >
                {isDone ? <Check className="h-3.5 w-3.5" /> : stepNum}
              </span>
              {label}
            </div>
            {i < STEPS.length - 1 && (
              <div className="h-px w-6 bg-neutral-200" aria-hidden="true" />
            )}
          </div>
        );
      })}
    </nav>
  );
}

// ── Stay strip ─────────────────────────────────────────────────────────────────

function StayStrip({ values, nights }: { values: Step1Values; nights: number }) {
  return (
    <div className="flex flex-wrap items-center gap-4 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 text-sm">
      <span className="font-medium text-neutral-900">{values.hotelName}</span>
      <span className="text-neutral-400">·</span>
      <span className="text-neutral-700">{values.clientName}</span>
      <span className="text-neutral-400">·</span>
      <span className="text-neutral-700">
        {values.checkIn} → {values.checkOut}
      </span>
      <span className="text-neutral-400">·</span>
      <span className="font-medium text-[#F57C1F]">
        {nights} night{nights !== 1 ? 's' : ''}
      </span>
    </div>
  );
}

// ── Rate badge ─────────────────────────────────────────────────────────────────

function RateBadge({ source }: { source: RoomRow['rateSource'] }) {
  if (source === 'unset') return null;
  if (source === 'contract') {
    return (
      <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
        Contract rate
      </span>
    );
  }
  return (
    <span className="inline-flex items-center rounded-full bg-[#FFF3E8] px-2 py-0.5 text-xs font-medium text-[#F57C1F]">
      {source === 'adhoc' ? 'Ad-hoc rate' : 'No contract — ad-hoc'}
    </span>
  );
}

// ── Segment disclosure ─────────────────────────────────────────────────────────

function SegmentDisclosure({ segments }: { segments: NonNullable<LookupResult['segments']> }) {
  const [open, setOpen] = useState(false);
  if (segments.length <= 1) return null;
  return (
    <div className="mt-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-blue-600 hover:underline"
      >
        Rate spans {segments.length} periods
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
      </button>
      {open && (
        <table className="mt-1 w-full border-collapse text-xs">
          <thead>
            <tr className="bg-neutral-50">
              <th className="border border-neutral-200 px-2 py-1 text-left font-medium">From</th>
              <th className="border border-neutral-200 px-2 py-1 text-left font-medium">To</th>
              <th className="border border-neutral-200 px-2 py-1 text-right font-medium">Rate</th>
              <th className="border border-neutral-200 px-2 py-1 text-right font-medium">Nights</th>
            </tr>
          </thead>
          <tbody>
            {segments.map((seg, i) => (
              <tr key={i}>
                <td className="border border-neutral-200 px-2 py-1">{seg.from}</td>
                <td className="border border-neutral-200 px-2 py-1">{seg.to}</td>
                <td className="border border-neutral-200 px-2 py-1 text-right tabular-nums">
                  {formatINR(seg.rate)}
                </td>
                <td className="border border-neutral-200 px-2 py-1 text-right tabular-nums">
                  {seg.nights}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ── Room row editor ────────────────────────────────────────────────────────────

function RoomRowEditor({
  row,
  rowIndex,
  roomTypes,
  nights,
  canDelete,
  onUpdate,
  onDelete,
}: {
  row: RoomRow;
  rowIndex: number;
  roomTypes: RoomType[];
  nights: number;
  canDelete: boolean;
  onUpdate: (id: string, patch: Partial<RoomRow>) => void;
  onDelete: (id: string) => void;
}) {
  const selectedRoomType = roomTypes.find((rt) => rt.id === row.hotelRoomTypeId) ?? null;
  const maxOcc = selectedRoomType?.maxOccupancy ?? null;

  const guestsExceedsMax = maxOcc !== null && row.guestsPerRoom > maxOcc;
  const amount =
    row.ratePerNight !== '' && nights > 0
      ? (row.ratePerNight as number) * nights * row.roomsCount
      : 0;

  const isAdhoc = row.rateSource === 'adhoc';

  return (
    <tr className={cn('align-top', isAdhoc ? 'bg-[#FFF9F5]' : '')}>
      {/* Adhoc indicator */}
      <td className="border-b border-neutral-100 px-2 py-2 align-middle">
        {isAdhoc && (
          <span className="inline-block rounded-full bg-[#F57C1F] px-1.5 py-0.5 text-[10px] font-semibold text-white">
            AD-HOC
          </span>
        )}
        <span className="block text-xs text-neutral-400">{rowIndex + 1}</span>
      </td>

      {/* Room type */}
      <td className="border-b border-neutral-100 px-2 py-2">
        <Select
          value={row.hotelRoomTypeId}
          onValueChange={(v) => {
            const rt = roomTypes.find((r) => r.id === v);
            onUpdate(row.id, {
              hotelRoomTypeId: v,
              maxOccupancy: rt?.maxOccupancy ?? null,
              ratePerNight: '',
              rateSource: 'unset',
              contractedRateId: null,
              segments: undefined,
            });
          }}
        >
          <SelectTrigger className="h-8 min-w-[140px] text-xs">
            <SelectValue placeholder="Select room type" />
          </SelectTrigger>
          <SelectContent>
            {roomTypes.map((rt) => (
              <SelectItem key={rt.id} value={rt.id} className="text-xs">
                {rt.name}
                {rt.maxOccupancy != null ? ` (max ${rt.maxOccupancy})` : ''}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>

      {/* Meal plan */}
      <td className="border-b border-neutral-100 px-2 py-2">
        <Select
          value={row.mealPlan}
          onValueChange={(v) =>
            onUpdate(row.id, {
              mealPlan: v,
              ratePerNight: '',
              rateSource: 'unset',
              contractedRateId: null,
              segments: undefined,
            })
          }
        >
          <SelectTrigger className="h-8 w-[90px] text-xs">
            <SelectValue placeholder="Meal" />
          </SelectTrigger>
          <SelectContent>
            {MEAL_PLANS.map((mp) => (
              <SelectItem key={mp} value={mp} className="text-xs">
                {mp}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>

      {/* Rooms count */}
      <td className="border-b border-neutral-100 px-2 py-2">
        <Input
          type="number"
          min={1}
          className="h-8 w-16 text-xs tabular-nums"
          value={row.roomsCount}
          onChange={(e) =>
            onUpdate(row.id, { roomsCount: Math.max(1, parseInt(e.target.value) || 1) })
          }
        />
      </td>

      {/* Occupancy */}
      <td className="border-b border-neutral-100 px-2 py-2">
        <Select
          value={row.occupancyType}
          onValueChange={(v) =>
            onUpdate(row.id, {
              occupancyType: v,
              ratePerNight: '',
              rateSource: 'unset',
              contractedRateId: null,
              segments: undefined,
            })
          }
        >
          <SelectTrigger className="h-8 w-[100px] text-xs">
            <SelectValue placeholder="Occupancy" />
          </SelectTrigger>
          <SelectContent>
            {OCCUPANCY_TYPES.map((ot) => (
              <SelectItem key={ot} value={ot} className="text-xs capitalize">
                {ot}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </td>

      {/* Guests/room */}
      <td className="border-b border-neutral-100 px-2 py-2">
        <Input
          type="number"
          min={1}
          className={cn('h-8 w-16 text-xs tabular-nums', guestsExceedsMax ? 'border-red-500' : '')}
          value={row.guestsPerRoom}
          onChange={(e) =>
            onUpdate(row.id, { guestsPerRoom: Math.max(1, parseInt(e.target.value) || 1) })
          }
        />
        {guestsExceedsMax && (
          <p className="mt-0.5 text-[10px] text-red-600">Exceeds room max ({maxOcc}).</p>
        )}
      </td>

      {/* Rate/night */}
      <td className="border-b border-neutral-100 px-2 py-2">
        {row.isLookupLoading ? (
          <div className="flex h-8 w-[110px] items-center gap-1 rounded-md border border-neutral-200 px-2 text-xs text-neutral-400">
            <Loader2 className="h-3 w-3 animate-spin" /> Looking up…
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <span className="text-xs text-neutral-500">₹</span>
            <Input
              type="number"
              min={0}
              className="h-8 w-[90px] text-xs tabular-nums"
              value={row.ratePerNight}
              onChange={(e) => {
                const val = e.target.value === '' ? '' : parseFloat(e.target.value);
                onUpdate(row.id, applyRateChange(row, val));
              }}
            />
          </div>
        )}
        <div className="mt-1">
          <RateBadge source={row.rateSource} />
        </div>
        {row.segments && row.segments.length > 1 && (
          <SegmentDisclosure segments={row.segments} />
        )}
        {/* Ad-hoc reason — optional note for any adhoc rate */}
        {row.rateSource === 'adhoc' && (
          <div className="mt-1">
            <Textarea
              placeholder={
                row.contractedRateId
                  ? 'Override reason (optional)'
                  : 'Reason for ad-hoc rate (optional)'
              }
              rows={2}
              className="min-h-0 w-full text-xs"
              value={row.rateOverrideReason}
              onChange={(e) => onUpdate(row.id, { rateOverrideReason: e.target.value })}
            />
          </div>
        )}
      </td>

      {/* Amount */}
      <td className="border-b border-neutral-100 px-2 py-2 text-right text-xs tabular-nums">
        {amount > 0 ? formatINR(amount) : '—'}
      </td>

      {/* Delete */}
      <td className="border-b border-neutral-100 px-2 py-2 text-center">
        <button
          type="button"
          disabled={!canDelete}
          onClick={() => onDelete(row.id)}
          className={cn(
            'rounded p-1 text-neutral-300 transition-colors hover:bg-red-50 hover:text-red-600',
            !canDelete && 'pointer-events-none opacity-30',
          )}
          title="Remove row"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </td>
    </tr>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

export function NewBookingPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  // Wizard step: 1..4
  const [step, setStep] = useState(1);

  // ── Step 1 form ──────────────────────────────────────────────────────────
  const [hotelSearch, setHotelSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');

  const defaultCheckIn = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  }, []);

  const defaultCheckOut = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + 2);
    return d.toISOString().slice(0, 10);
  }, []);

  const step1Form = useForm<Step1Values>({
    resolver: zodResolver(step1Schema),
    defaultValues: {
      hotelId: '',
      hotelName: '',
      hotelCity: '',
      clientId: '',
      clientName: '',
      clientType: '',
      checkIn: defaultCheckIn,
      checkOut: defaultCheckOut,
    },
  });

  // ── Step 3 form ──────────────────────────────────────────────────────────
  const step3Form = useForm<Step3Values>({
    resolver: zodResolver(step3Schema),
    defaultValues: {
      guestName: '',
      confNo: '',
      initialStatus: 'draft',
      tentativeUntil: '',
      remark: '',
    },
  });

  const watchedStatus = step3Form.watch('initialStatus');

  // ── Room rows (Step 2) ───────────────────────────────────────────────────
  const [rows, setRows] = useState<RoomRow[]>([newRow()]);

  // ── Load draft from localStorage ─────────────────────────────────────────
  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw) as {
        step1?: Partial<Step1Values>;
        step3?: Partial<Step3Values>;
        rows?: RoomRow[];
        hotelSearch?: string;
        clientSearch?: string;
      };
      if (draft.step1) {
        step1Form.reset({ ...step1Form.getValues(), ...draft.step1 });
        if (draft.hotelSearch) setHotelSearch(draft.hotelSearch);
        if (draft.clientSearch) setClientSearch(draft.clientSearch);
      }
      if (draft.step3) step3Form.reset({ ...step3Form.getValues(), ...draft.step3 });
      if (draft.rows?.length) {
        setRows(
          draft.rows.map((r) => ({ ...r, isLookupLoading: false })),
        );
      }
    } catch {
      // ignore parse errors
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist draft on every change
  const step1Values = step1Form.watch();
  const step3Values = step3Form.watch();

  useEffect(() => {
    try {
      localStorage.setItem(
        DRAFT_KEY,
        JSON.stringify({ step1: step1Values, step3: step3Values, rows, hotelSearch, clientSearch }),
      );
    } catch {
      // ignore quota errors
    }
  }, [step1Values, step3Values, rows, hotelSearch, clientSearch]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const nights = useMemo(
    () => nightsCount(step1Values.checkIn, step1Values.checkOut),
    [step1Values.checkIn, step1Values.checkOut],
  );

  // ── Hotel list (fetch all, filter client-side) ────────────────────────────
  const { data: allHotels = [], isFetching: hotelsLoading } = useQuery({
    queryKey: ['hotels'],
    queryFn: () => api.get<{ id: string; name: string; city: string }[]>('/hotels'),
    staleTime: 60_000,
  });

  const hotelOptions: ComboOption[] = useMemo(() => {
    const q = hotelSearch.toLowerCase().trim();
    return allHotels
      .filter((h) => !q || h.name.toLowerCase().includes(q) || h.city.toLowerCase().includes(q))
      .slice(0, 50)
      .map((h) => ({ id: h.id, label: h.name, sublabel: h.city }));
  }, [allHotels, hotelSearch]);

  // ── Client list (fetch all, filter client-side) ───────────────────────────
  const { data: allClients = [], isFetching: clientsLoading } = useQuery({
    queryKey: ['clients'],
    queryFn: () => api.get<{ id: string; name: string; type: string }[]>('/clients'),
    staleTime: 60_000,
  });

  const clientOptions: ComboOption[] = useMemo(() => {
    const q = clientSearch.toLowerCase().trim();
    return allClients
      .filter((c) => !q || c.name.toLowerCase().includes(q) || c.type.toLowerCase().includes(q))
      .slice(0, 50)
      .map((c) => ({ id: c.id, label: c.name, sublabel: c.type }));
  }, [allClients, clientSearch]);

  // ── Room types for selected hotel ─────────────────────────────────────────
  const selectedHotelId = step1Values.hotelId;

  const { data: roomTypesData } = useQuery({
    queryKey: ['room-types', selectedHotelId],
    queryFn: () =>
      api.get<RoomType[]>(`/hotels/${selectedHotelId}/room-types?status=active`),
    enabled: !!selectedHotelId,
    staleTime: 60_000,
  });

  const roomTypes: RoomType[] = roomTypesData ?? [];

  // ── Rate lookup ───────────────────────────────────────────────────────────
  // Track which rows have pending lookups. We debounce per-row.
  const lookupTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const doLookup = useCallback(
    async (rowId: string, row: RoomRow) => {
      if (
        !row.hotelRoomTypeId ||
        !row.mealPlan ||
        !row.occupancyType ||
        !step1Values.hotelId ||
        !step1Values.clientId ||
        !step1Values.checkIn ||
        !step1Values.checkOut
      )
        return;

      setRows((prev) =>
        prev.map((r) => (r.id === rowId ? { ...r, isLookupLoading: true } : r)),
      );

      try {
        const params = new URLSearchParams({
          hotelId: step1Values.hotelId,
          clientId: step1Values.clientId,
          hotelRoomTypeId: row.hotelRoomTypeId,
          mealPlan: row.mealPlan,
          occupancy: row.occupancyType,
          checkIn: step1Values.checkIn,
          checkOut: step1Values.checkOut,
        });
        const result = await api.get<LookupResult>(
          `/contracted-rates/lookup?${params.toString()}`,
        );

        setRows((prev) =>
          prev.map((r) => {
            if (r.id !== rowId) return r;
            if (result.matched && result.ratePerNight != null) {
              return {
                ...r,
                ratePerNight: result.ratePerNight,
                rateSource: 'contract',
                contractedRateId: result.rateId ?? null,
                segments: result.segments,
                isLookupLoading: false,
              };
            } else {
              return {
                ...r,
                rateSource: 'adhoc',
                contractedRateId: null,
                segments: undefined,
                isLookupLoading: false,
              };
            }
          }),
        );
      } catch {
        setRows((prev) =>
          prev.map((r) =>
            r.id === rowId ? { ...r, isLookupLoading: false, rateSource: 'adhoc' } : r,
          ),
        );
      }
    },
    [step1Values],
  );

  // ── Row update handler ────────────────────────────────────────────────────
  const updateRow = useCallback(
    (id: string, patch: Partial<RoomRow>) => {
      setRows((prev) => {
        const next = prev.map((r) => (r.id === id ? { ...r, ...patch } : r));

        // Check if we should trigger a lookup
        const updatedRow = next.find((r) => r.id === id);
        if (!updatedRow) return next;

        const triggerKeys: Array<keyof RoomRow> = ['hotelRoomTypeId', 'mealPlan', 'occupancyType'];
        const shouldLookup =
          triggerKeys.some((k) => k in patch) &&
          updatedRow.hotelRoomTypeId &&
          updatedRow.mealPlan &&
          updatedRow.occupancyType;

        if (shouldLookup) {
          if (lookupTimers.current[id]) clearTimeout(lookupTimers.current[id]);
          lookupTimers.current[id] = setTimeout(() => {
            void doLookup(id, updatedRow);
          }, 300);
        }

        return next;
      });
    },
    [doLookup],
  );

  const deleteRow = useCallback((id: string) => {
    setRows((prev) => (prev.length > 1 ? prev.filter((r) => r.id !== id) : prev));
  }, []);

  const addRow = useCallback(() => {
    setRows((prev) => [...prev, newRow()]);
  }, []);

  // ── Step 2 validation ─────────────────────────────────────────────────────
  const step2HasErrors = useMemo(
    () => rows.some((row) => rowHasError(row, roomTypes)),
    [rows, roomTypes],
  );

  // ── Totals ────────────────────────────────────────────────────────────────
  const totals = useMemo(() => computeTotals(rows, nights), [rows, nights]);

  // ── Submit mutation ───────────────────────────────────────────────────────
  const createMutation = useMutation({
    mutationFn: (body: unknown) => api.post<{ id: string }>('/bookings', body),
    onSuccess: (created) => {
      localStorage.removeItem(DRAFT_KEY);
      toast.success('Booking created');
      navigate(`/bookings/${created.id}`);
    },
    onError: (err) =>
      toast.error(err instanceof ApiError ? err.message : 'Failed to create booking'),
  });

  function handleSubmit() {
    const s1 = step1Form.getValues();
    const s3 = step3Form.getValues();

    const body = {
      hotelId: s1.hotelId,
      clientId: s1.clientId,
      guestName: s3.guestName,
      checkIn: s1.checkIn,
      checkOut: s1.checkOut,
      rooms: rows.map((row, i) => ({
        hotelRoomTypeId: row.hotelRoomTypeId,
        mealPlan: row.mealPlan,
        occupancyType: row.occupancyType,
        guestsPerRoom: row.guestsPerRoom,
        roomsCount: row.roomsCount,
        ratePerNight: row.ratePerNight as number,
        rateSource: row.rateSource === 'contract' ? 'contract' : 'adhoc',
        ...(row.rateOverrideReason ? { rateOverrideReason: row.rateOverrideReason } : {}),
        ...(row.contractedRateId ? { contractedRateId: row.contractedRateId } : {}),
        sortOrder: i,
      })),
      initialStatus: s3.initialStatus,
      ...(s3.confNo ? { confNo: s3.confNo } : {}),
      ...(s3.tentativeUntil ? { tentativeUntil: s3.tentativeUntil } : {}),
      ...(s3.remark ? { remark: s3.remark } : {}),
    };

    createMutation.mutate(body);
  }

  // ── Navigation helpers ────────────────────────────────────────────────────
  async function goToStep2() {
    const valid = await step1Form.trigger();
    if (valid) setStep(2);
  }

  async function goToStep3() {
    if (!step2HasErrors) setStep(3);
  }

  async function goToStep4() {
    const valid = await step3Form.trigger();
    if (valid) setStep(4);
  }

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full flex-col">
      <PageHeader title="New Booking" description="Create a new hotel booking" />
      <StepIndicator current={step} />

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-4xl px-6 py-8">
          {/* ── Step 1: Foundation ─────────────────────────────────────── */}
          {step === 1 && (
            <div className="space-y-6">
              <BackLink to="/bookings" label="Back to bookings" />
              <div className="rounded-lg border border-neutral-200 bg-white p-6">
                <h2 className="mb-5 text-base font-semibold text-neutral-900">Booking foundation</h2>

                <div className="space-y-4">
                  {/* Hotel */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-neutral-700">
                      Hotel <span className="text-red-600">*</span>
                    </label>
                    <Combobox
                      placeholder="Search hotels…"
                      value={
                        step1Values.hotelId
                          ? { id: step1Values.hotelId, label: step1Values.hotelName, sublabel: step1Values.hotelCity }
                          : null
                      }
                      onChange={(opt) => {
                        if (opt) {
                          step1Form.setValue('hotelId', opt.id, { shouldValidate: true });
                          step1Form.setValue('hotelName', opt.label);
                          step1Form.setValue('hotelCity', opt.sublabel ?? '');
                          setHotelSearch(opt.label);
                          // Clear room type selections
                          setRows((prev) =>
                            prev.map((r) => ({
                              ...r,
                              hotelRoomTypeId: '',
                              ratePerNight: '',
                              rateSource: 'unset',
                              contractedRateId: null,
                              segments: undefined,
                            })),
                          );
                        } else {
                          step1Form.setValue('hotelId', '', { shouldValidate: true });
                          step1Form.setValue('hotelName', '');
                          step1Form.setValue('hotelCity', '');
                          setHotelSearch('');
                        }
                      }}
                      options={hotelOptions}
                      isLoading={hotelsLoading}
                      onSearch={(q) => {
                        setHotelSearch(q);
                        if (!q) {
                          step1Form.setValue('hotelId', '', { shouldValidate: false });
                          step1Form.setValue('hotelName', '');
                          step1Form.setValue('hotelCity', '');
                        }
                      }}
                      searchValue={hotelSearch}
                    />
                    {step1Form.formState.errors.hotelId && (
                      <p className="text-xs text-red-600">{step1Form.formState.errors.hotelId.message}</p>
                    )}
                  </div>

                  {/* Client */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-neutral-700">
                      Client <span className="text-red-600">*</span>
                    </label>
                    <Combobox
                      placeholder="Search clients…"
                      value={
                        step1Values.clientId
                          ? { id: step1Values.clientId, label: step1Values.clientName, sublabel: step1Values.clientType }
                          : null
                      }
                      onChange={(opt) => {
                        if (opt) {
                          step1Form.setValue('clientId', opt.id, { shouldValidate: true });
                          step1Form.setValue('clientName', opt.label);
                          step1Form.setValue('clientType', opt.sublabel ?? '');
                          setClientSearch(opt.label);
                        } else {
                          step1Form.setValue('clientId', '', { shouldValidate: true });
                          step1Form.setValue('clientName', '');
                          step1Form.setValue('clientType', '');
                          setClientSearch('');
                        }
                      }}
                      options={clientOptions}
                      isLoading={clientsLoading}
                      onSearch={(q) => {
                        setClientSearch(q);
                        if (!q) {
                          step1Form.setValue('clientId', '', { shouldValidate: false });
                          step1Form.setValue('clientName', '');
                          step1Form.setValue('clientType', '');
                        }
                      }}
                      searchValue={clientSearch}
                    />
                    {step1Form.formState.errors.clientId && (
                      <p className="text-xs text-red-600">{step1Form.formState.errors.clientId.message}</p>
                    )}
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1.5">
                      <label htmlFor="check-in" className="text-sm font-medium text-neutral-700">
                        Check-in <span className="text-red-600">*</span>
                      </label>
                      <Input
                        id="check-in"
                        type="date"
                        {...step1Form.register('checkIn')}
                      />
                      {step1Form.formState.errors.checkIn && (
                        <p className="text-xs text-red-600">{step1Form.formState.errors.checkIn.message}</p>
                      )}
                    </div>
                    <div className="space-y-1.5">
                      <label htmlFor="check-out" className="text-sm font-medium text-neutral-700">
                        Check-out <span className="text-red-600">*</span>
                      </label>
                      <Input
                        id="check-out"
                        type="date"
                        {...step1Form.register('checkOut')}
                      />
                      {step1Form.formState.errors.checkOut && (
                        <p className="text-xs text-red-600">{step1Form.formState.errors.checkOut.message}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={() => void goToStep2()}>
                  Next: Rooms →
                </Button>
              </div>
            </div>
          )}

          {/* ── Step 2: Rooms ──────────────────────────────────────────── */}
          {step === 2 && (
            <div className="space-y-4">
              <StayStrip values={step1Values} nights={nights} />

              <div className="rounded-lg border border-neutral-200 bg-white">
                <div className="border-b border-neutral-100 px-4 py-3">
                  <h2 className="text-sm font-semibold text-neutral-700">Room configurations</h2>
                </div>

                {roomTypes.length === 0 && step1Values.hotelId && (
                  <p className="px-4 py-3 text-sm text-neutral-400">
                    No active room types found for this hotel.
                  </p>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full border-collapse text-sm" style={{ minWidth: '860px' }}>
                    <thead className="bg-neutral-50 text-xs text-neutral-500">
                      <tr>
                        <th className="border-b border-neutral-100 px-2 py-2 text-left font-medium w-12">#</th>
                        <th className="border-b border-neutral-100 px-2 py-2 text-left font-medium">Room Type</th>
                        <th className="border-b border-neutral-100 px-2 py-2 text-left font-medium">Meal Plan</th>
                        <th className="border-b border-neutral-100 px-2 py-2 text-left font-medium">Rooms</th>
                        <th className="border-b border-neutral-100 px-2 py-2 text-left font-medium">Occupancy</th>
                        <th className="border-b border-neutral-100 px-2 py-2 text-left font-medium">Guests/Rm</th>
                        <th className="border-b border-neutral-100 px-2 py-2 text-left font-medium">Rate/Night</th>
                        <th className="border-b border-neutral-100 px-2 py-2 text-right font-medium">Amount</th>
                        <th className="border-b border-neutral-100 px-2 py-2 w-10" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, i) => (
                        <RoomRowEditor
                          key={row.id}
                          row={row}
                          rowIndex={i}
                          roomTypes={roomTypes}
                          nights={nights}
                          canDelete={rows.length > 1}
                          onUpdate={updateRow}
                          onDelete={deleteRow}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Add row */}
                <div className="border-t border-neutral-100 px-4 py-3">
                  <Button type="button" variant="outline" size="sm" onClick={addRow}>
                    + Add room
                  </Button>
                </div>

                {/* Totals strip */}
                <div className="border-t border-neutral-200 bg-neutral-50 px-4 py-3 flex flex-wrap items-center justify-between gap-4 text-sm">
                  <div className="flex items-center gap-6 text-neutral-600">
                    <span>
                      <span className="font-medium">{totals.totalRooms}</span> total rooms
                    </span>
                    <span>
                      <span className="font-medium">{totals.totalRoomNights}</span> room-nights
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-neutral-500 mr-2">Grand total</span>
                    <span className="text-base font-semibold text-neutral-900">
                      {formatINR(totals.grandTotal)}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Button variant="outline" onClick={() => setStep(1)}>
                  ← Back
                </Button>
                <Button
                  onClick={() => void goToStep3()}
                  disabled={step2HasErrors}
                  title={step2HasErrors ? 'Fix errors in room rows to continue' : undefined}
                >
                  Next: Details →
                </Button>
              </div>
            </div>
          )}

          {/* ── Step 3: Booking details ────────────────────────────────── */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="rounded-lg border border-neutral-200 bg-white p-6">
                <h2 className="mb-5 text-base font-semibold text-neutral-900">Booking details</h2>

                <div className="space-y-4">
                  {/* Guest name */}
                  <div className="space-y-1.5">
                    <label htmlFor="guest-name" className="text-sm font-medium text-neutral-700">
                      Guest name <span className="text-red-600">*</span>
                    </label>
                    <Input
                      id="guest-name"
                      placeholder="Lead guest's full name"
                      {...step3Form.register('guestName')}
                    />
                    {step3Form.formState.errors.guestName && (
                      <p className="text-xs text-red-600">{step3Form.formState.errors.guestName.message}</p>
                    )}
                  </div>

                  {/* Conf no */}
                  <div className="space-y-1.5">
                    <label htmlFor="conf-no" className="text-sm font-medium text-neutral-700">
                      Confirmation no. <span className="text-neutral-400 text-xs">(optional)</span>
                    </label>
                    <Input
                      id="conf-no"
                      placeholder="Hotel confirmation number"
                      {...step3Form.register('confNo')}
                    />
                  </div>

                  {/* Booker (read-only) */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-neutral-700">Booker</label>
                    <Input
                      value={user?.fullName ?? '—'}
                      readOnly
                      className="bg-neutral-50 text-neutral-500"
                    />
                  </div>

                  {/* Initial status */}
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-neutral-700">Initial status</label>
                    <div className="flex items-center gap-4">
                      {(['draft', 'tentative', 'confirmed'] as const).map((s) => (
                        <label key={s} className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="radio"
                            value={s}
                            {...step3Form.register('initialStatus')}
                            className="accent-[#F57C1F]"
                          />
                          <span className="text-sm capitalize text-neutral-700">{s}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Tentative until — shown only when status = tentative */}
                  {watchedStatus === 'tentative' && (
                    <div className="space-y-1.5">
                      <label htmlFor="tentative-until" className="text-sm font-medium text-neutral-700">
                        Tentative until <span className="text-red-600">*</span>
                      </label>
                      <Input
                        id="tentative-until"
                        type="date"
                        min={step1Values.checkOut || new Date().toISOString().slice(0, 10)}
                        {...step3Form.register('tentativeUntil')}
                      />
                      {step3Form.formState.errors.tentativeUntil && (
                        <p className="text-xs text-red-600">{step3Form.formState.errors.tentativeUntil.message}</p>
                      )}
                    </div>
                  )}

                  {/* Remark */}
                  <div className="space-y-1.5">
                    <label htmlFor="remark" className="text-sm font-medium text-neutral-700">
                      Remark <span className="text-neutral-400 text-xs">(optional)</span>
                    </label>
                    <Textarea
                      id="remark"
                      placeholder="Any additional notes…"
                      rows={3}
                      {...step3Form.register('remark')}
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <Button variant="outline" onClick={() => setStep(2)}>
                  ← Back
                </Button>
                <Button onClick={() => void goToStep4()}>
                  Next: Review →
                </Button>
              </div>
            </div>
          )}

          {/* ── Step 4: Review ─────────────────────────────────────────── */}
          {step === 4 && (
            <ReviewStep
              step1={step1Values}
              step3={step3Form.getValues()}
              rows={rows}
              roomTypes={roomTypes}
              nights={nights}
              totals={totals}
              bookerName={user?.fullName ?? '—'}
              isPending={createMutation.isPending}
              onBack={() => setStep(3)}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ── Review step (extracted for clarity) ───────────────────────────────────────

function ReviewStep({
  step1,
  step3,
  rows,
  roomTypes,
  nights,
  totals,
  bookerName,
  isPending,
  onBack,
  onSubmit,
}: {
  step1: Step1Values;
  step3: Step3Values;
  rows: RoomRow[];
  roomTypes: RoomType[];
  nights: number;
  totals: { totalRooms: number; totalRoomNights: number; grandTotal: number };
  bookerName: string;
  isPending: boolean;
  onBack: () => void;
  onSubmit: () => void;
}) {
  function InfoRow({ label, value }: { label: string; value?: React.ReactNode }) {
    return (
      <div className="flex gap-4 border-b border-neutral-100 py-2 last:border-0">
        <span className="w-36 shrink-0 text-sm text-neutral-500">{label}</span>
        <span className="text-sm text-neutral-900">{value ?? '—'}</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Summary card */}
      <div className="rounded-lg border border-neutral-200 bg-white">
        <div className="border-b border-neutral-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-neutral-700">Summary</h3>
        </div>
        <div className="px-4 py-2">
          <InfoRow label="Hotel" value={`${step1.hotelName}${step1.hotelCity ? ` · ${step1.hotelCity}` : ''}`} />
          <InfoRow label="Client" value={step1.clientName} />
          <InfoRow label="Check-in" value={step1.checkIn} />
          <InfoRow label="Check-out" value={step1.checkOut} />
          <InfoRow label="Nights" value={nights} />
          <InfoRow label="Lead guest" value={step3.guestName} />
          <InfoRow label="Conf. no." value={step3.confNo || '—'} />
          <InfoRow label="Booker" value={bookerName} />
          <InfoRow
            label="Initial status"
            value={
              <span className="capitalize">{step3.initialStatus}</span>
            }
          />
          {step3.tentativeUntil && (
            <InfoRow label="Tentative until" value={step3.tentativeUntil} />
          )}
          {step3.remark && <InfoRow label="Remark" value={step3.remark} />}
        </div>
      </div>

      {/* Room cards */}
      {rows.map((row, i) => {
        const rt = roomTypes.find((r) => r.id === row.hotelRoomTypeId);
        const amount =
          row.ratePerNight !== '' ? (row.ratePerNight as number) * nights * row.roomsCount : 0;
        const isAdhoc = row.rateSource === 'adhoc';

        return (
          <div key={row.id} className="rounded-lg border border-neutral-200 bg-white">
            <div className="flex items-center justify-between border-b border-neutral-100 px-4 py-3">
              <h3 className="text-sm font-semibold text-neutral-700">
                Room {i + 1}: {rt?.name ?? row.hotelRoomTypeId}
              </h3>
              {isAdhoc && (
                <span className="inline-flex items-center rounded-full bg-[#FFF3E8] px-2 py-0.5 text-xs font-medium text-[#F57C1F]">
                  Ad-hoc rate
                </span>
              )}
            </div>
            <div className="px-4 py-2 text-sm">
              <InfoRow label="Meal plan" value={row.mealPlan} />
              <InfoRow label="Occupancy" value={<span className="capitalize">{row.occupancyType}</span>} />
              <InfoRow label="Guests/room" value={row.guestsPerRoom} />
              <InfoRow label="Rooms" value={row.roomsCount} />
              <InfoRow label="Rate/night" value={row.ratePerNight !== '' ? formatINR(row.ratePerNight as number) : '—'} />
              <InfoRow label="Amount" value={<span className="font-semibold">{formatINR(amount)}</span>} />
              {isAdhoc && row.rateOverrideReason && (
                <InfoRow label="Reason" value={row.rateOverrideReason} />
              )}
            </div>
          </div>
        );
      })}

      {/* Totals card */}
      <div className="rounded-lg border border-neutral-200 bg-white">
        <div className="border-b border-neutral-100 px-4 py-3">
          <h3 className="text-sm font-semibold text-neutral-700">Totals</h3>
        </div>
        <div className="px-4 py-2 text-sm">
          <InfoRow label="Total rooms" value={totals.totalRooms} />
          <InfoRow label="Total room-nights" value={totals.totalRoomNights} />
          <InfoRow
            label="Grand total"
            value={
              <span className="text-base font-bold text-neutral-900">
                {formatINR(totals.grandTotal)}
              </span>
            }
          />
        </div>
      </div>

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={onBack} disabled={isPending}>
          ← Back
        </Button>
        <Button
          onClick={onSubmit}
          disabled={isPending}
          className="bg-[#F57C1F] text-white hover:bg-[#E06600] active:bg-[#C85A00] min-w-[160px] justify-center"
        >
          {isPending ? (
            <span className="flex items-center gap-2">
              <Spinner className="h-4 w-4" /> Creating…
            </span>
          ) : (
            'Create booking'
          )}
        </Button>
      </div>
    </div>
  );
}
